import { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Loader2, Plus, Warehouse as WarehouseIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export default function WarehousesPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [warehouseForm, setWarehouseForm] = useState({
    name: '',
    code: '',
    address: '',
    warehouse_type: 'SALES',
    tag: 'SALES',
    is_active: true,
    is_default_sales: false,
    is_default_return: false,
  });

  const fetchWarehouses = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getWarehouses();
      setWarehouses(response.data || []);
    } catch (error) {
      toast.error('Failed to load warehouses');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setWarehouseForm({
      name: '',
      code: '',
      address: '',
      warehouse_type: 'SALES',
      tag: 'SALES',
      is_active: true,
      is_default_sales: false,
      is_default_return: false,
    });
  };

  const handleCreateWarehouse = async () => {
    if (!warehouseForm.name.trim()) {
      toast.error('Please enter warehouse name');
      return;
    }

    setSaving(true);
    try {
      await adminApi.createWarehouse({
        ...warehouseForm,
        code: warehouseForm.code || undefined,
        address: warehouseForm.address || '',
      });
      toast.success('Warehouse created');
      setShowAddModal(false);
      resetForm();
      fetchWarehouses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create warehouse');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchWarehouses();
  }, []);

  return (
    <div className="space-y-6" data-testid="warehouses-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Warehouses</h1>
          <p className="text-[#A8A8A8] mt-1">View and manage configured warehouses</p>
        </div>
        <Button onClick={() => { resetForm(); setShowAddModal(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Add Warehouse
        </Button>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
            </div>
          ) : warehouses.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-[#A8A8A8]">
              <WarehouseIcon className="w-6 h-6 mr-2" />
              No warehouses yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Name</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Code</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Type</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Tag</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Default</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {warehouses.map((warehouse) => (
                    <tr key={warehouse.id} className="hover:bg-[#FAFAF9]/60">
                      <td className="py-3 px-4 text-[#2D2420] font-medium">{warehouse.name}</td>
                      <td className="py-3 px-4 text-[#A8A8A8]">{warehouse.code || '-'}</td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">
                        {warehouse.warehouse_type === 'RETURN' ? 'Return' : 'Sales'}
                      </td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{warehouse.tag || '-'}</td>
                      <td className="py-3 px-4">
                        <Badge className={cn('text-xs', warehouse.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800')}>
                          {warehouse.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        {warehouse.is_default_sales ? (
                          <Badge className="text-xs bg-[#C97A5E]/10 text-[#C97A5E]">Default Sales</Badge>
                        ) : warehouse.is_default_return ? (
                          <Badge className="text-xs bg-[#C97A5E]/10 text-[#C97A5E]">Default Return</Badge>
                        ) : (
                          <span className="text-xs text-[#A8A8A8]">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Add Warehouse</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={warehouseForm.name}
                onChange={(e) => setWarehouseForm((prev) => ({ ...prev, name: e.target.value }))}
                className="border-[#E5E5E5]"
              />
            </div>
            <div>
              <Label>Code</Label>
              <Input
                value={warehouseForm.code}
                onChange={(e) => setWarehouseForm((prev) => ({ ...prev, code: e.target.value }))}
                className="border-[#E5E5E5]"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={warehouseForm.address}
                onChange={(e) => setWarehouseForm((prev) => ({ ...prev, address: e.target.value }))}
                className="border-[#E5E5E5]"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select
                value={warehouseForm.warehouse_type}
                onValueChange={(value) => setWarehouseForm((prev) => ({
                  ...prev,
                  warehouse_type: value,
                  tag: value === 'RETURN' ? 'RETURN' : (prev.tag === 'RETURN' ? 'SALES' : prev.tag),
                  is_default_sales: value === 'RETURN' ? false : prev.is_default_sales,
                  is_default_return: value === 'RETURN' ? prev.is_default_return : false,
                }))}
              >
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SALES">Sales</SelectItem>
                  <SelectItem value="RETURN">Return</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Tag</Label>
              <Select
                value={warehouseForm.tag}
                onValueChange={(value) => setWarehouseForm((prev) => ({ ...prev, tag: value }))}
                disabled={warehouseForm.warehouse_type === 'RETURN'}
              >
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {warehouseForm.warehouse_type === 'RETURN' ? (
                    <SelectItem value="RETURN">RETURN</SelectItem>
                  ) : (
                    <>
                      <SelectItem value="MAIN">MAIN</SelectItem>
                      <SelectItem value="SALES">SALES</SelectItem>
                      <SelectItem value="PACKING">PACKING</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between border border-[#E5E5E5] rounded-lg px-3 py-2">
              <Label className="text-sm">Active</Label>
              <Switch
                checked={warehouseForm.is_active}
                onCheckedChange={(value) => setWarehouseForm((prev) => ({ ...prev, is_active: value }))}
              />
            </div>

            {warehouseForm.warehouse_type === 'RETURN' ? (
              <div className="flex items-center justify-between border border-[#E5E5E5] rounded-lg px-3 py-2">
                <Label className="text-sm">Default Return</Label>
                <Switch
                  checked={warehouseForm.is_default_return}
                  onCheckedChange={(value) => setWarehouseForm((prev) => ({ ...prev, is_default_return: value }))}
                />
              </div>
            ) : (
              <div className="flex items-center justify-between border border-[#E5E5E5] rounded-lg px-3 py-2">
                <Label className="text-sm">Default Sales</Label>
                <Switch
                  checked={warehouseForm.is_default_sales}
                  onCheckedChange={(value) => setWarehouseForm((prev) => ({ ...prev, is_default_sales: value }))}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleCreateWarehouse} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
